import { z } from 'zod';

import { canonicalList, canonicalString } from '../evidence/finding-capture';

// ---------------------------------------------------------------------------
// A product area: a name, and the code it is built from.
//
// Deliberately the smallest thing that can scope a scan. There is no customer
// journey, no step mapping, no proposal and no outcome here — an area exists
// to answer "which findings are mine", and every one of those would make it
// answer something else as well.
//
// Areas are AUTHORED, not inferred. Nothing derives one from the graph, and
// nothing in this module measures coverage or revenue for an area: the
// repository's Codecov and Stripe surfaces already own those numbers, and an
// area-shaped copy of them would be a second figure free to disagree.
// ---------------------------------------------------------------------------

export const MAX_AREA_PATH_PREFIXES = 32;
export const MAX_AREA_REFERENCES = 16;

/**
 * One revision of an area, as its author recorded it.
 *
 * Revisions are appended, never rewritten. A scan compared under revision 2
 * must still be readable against what revision 2 actually said, however the
 * area is described later — otherwise a boundary change silently rewrites the
 * basis of a comparison already made.
 */
export const AreaRevisionSchema = z
  .object({
    revision: z.number().int().positive(),
    recordedAt: z.iso.datetime(),
    /** An authoring principal, not a person record. */
    recordedBy: z.string().min(1).max(200),
  })
  .readonly();
export type AreaRevision = z.infer<typeof AreaRevisionSchema>;

/**
 * A path prefix, repository-relative and directory-shaped.
 *
 * Normalised to a trailing slash so `apps/web` cannot silently claim
 * `apps/web-kruxit/`: prefix matching on the bare string would, and the
 * difference is an entire second application's findings.
 */
export const AreaPathPrefixSchema = z
  .string()
  .min(1)
  .max(1024)
  .refine((value) => !value.startsWith('/'), {
    message: 'an area path prefix is repository-relative',
  })
  .refine((value) => !value.split('/').includes('..'), {
    message: 'an area path prefix may not escape the repository',
  });

/** Normalise a path prefix to the form membership is decided on. */
export function normaliseAreaPrefix(prefix: string): string {
  const trimmed = prefix.replaceAll(/^\/+|\/+$/gu, '');
  return trimmed === '' ? '' : `${trimmed}/`;
}

/**
 * Whether a repository-relative path lies inside an area.
 *
 * Directory-boundary aware by construction: membership is tested against the
 * normalised prefix, so `apps/web-kruxit/x.ts` belongs to `apps/web-kruxit/`
 * and never to `apps/web`.
 */
export function pathInArea(path: string, prefixes: readonly string[]): boolean {
  if (prefixes.length === 0) return false;
  return prefixes.some((prefix) => {
    const normalised = normaliseAreaPrefix(prefix);
    return normalised === '' ? true : path.startsWith(normalised);
  });
}

/** An optional pointer out of the product: an issue, a doc, a dashboard. */
export const AreaReferenceSchema = z
  .object({
    label: z.string().min(1).max(120),
    url: z.url().max(2048),
  })
  .readonly();
export type AreaReference = z.infer<typeof AreaReferenceSchema>;

/**
 * An area as AUTHORED — the document minus the revision the store assigns.
 *
 * Declared so the store can validate what it was sent before fingerprinting
 * it, rather than fingerprinting a string and hoping.
 */
export const AuthoredAreaSchema = z.object({
  areaId: z.string().min(1).max(120),
  name: z.string().min(1).max(200),
  repoKey: z.string().min(1).max(256),
  pathPrefixes: z
    .array(AreaPathPrefixSchema)
    .min(1)
    .max(MAX_AREA_PATH_PREFIXES),
  references: z.array(AreaReferenceSchema).max(MAX_AREA_REFERENCES).default([]),
});

export const ProductAreaSchema = z
  .object({
    areaId: z.string().min(1).max(120),
    name: z.string().min(1).max(200),
    /** `owner/name`, lower-cased. An area lives in exactly one repository. */
    repoKey: z.string().min(1).max(256),
    pathPrefixes: z
      .array(AreaPathPrefixSchema)
      .min(1)
      .max(MAX_AREA_PATH_PREFIXES),
    references: z
      .array(AreaReferenceSchema)
      .max(MAX_AREA_REFERENCES)
      .default([]),
    revision: AreaRevisionSchema,
  })
  .readonly();
export type ProductArea = z.infer<typeof ProductAreaSchema>;

/**
 * The exact scope one revision claimed, frozen onto the evidence that used it.
 *
 * A comparison records this rather than the area id alone. An area whose paths
 * were widened between two scans would otherwise make a set of findings that
 * were always there look newly introduced, and nothing in the stored record
 * would say why.
 */
export const AreaScopeSchema = z
  .object({
    areaId: z.string().min(1),
    revision: z.number().int().positive(),
    repoKey: z.string().min(1),
    pathPrefixes: z.array(z.string().min(1)),
  })
  .readonly();
export type AreaScope = z.infer<typeof AreaScopeSchema>;

/** The fingerprint of one scope, so two comparisons under it agree exactly. */
export function areaScopeKey(scope: Readonly<AreaScope>): string {
  return [
    'area-v1',
    canonicalString(scope.areaId),
    canonicalString(String(scope.revision)),
    canonicalString(scope.repoKey.toLowerCase()),
    // Each prefix length-prefixed in its own right. Joining with a comma
    // would make `['a/','b/']` and `['a/,b/']` one key, so a comparison could
    // be recorded against a boundary it was not made under.
    canonicalList(
      [...scope.pathPrefixes]
        .map((prefix) => normaliseAreaPrefix(prefix))
        .toSorted(),
    ),
  ].join(':');
}

export function scopeOf(area: Readonly<ProductArea>): AreaScope {
  return {
    areaId: area.areaId,
    revision: area.revision.revision,
    repoKey: area.repoKey,
    pathPrefixes: area.pathPrefixes.map((prefix) =>
      normaliseAreaPrefix(prefix),
    ),
  };
}

/**
 * The one area this deployment seeds for itself.
 *
 * Kruxit is an application inside a monorepo, which is exactly the shape an
 * area exists for: the repository is `sykeus/starter` and the product is one
 * directory within it. Seeding it makes the whole scan-to-fix path exercisable
 * against real evidence without anyone first authoring a context by hand.
 */
export const KRUXIT_SEED_AREA = {
  areaId: 'kruxit',
  name: 'Kruxit',
  repoKey: 'sykeus/starter',
  pathPrefixes: ['apps/web-kruxit/'],
} as const;

/**
 * The authored content a semantic fingerprint is taken over.
 *
 * Structural, so both the caller and the store can take the SAME fingerprint
 * from the same validated document — which is the point: a hash the caller
 * supplies is a claim about content, and idempotency decided on a claim lets
 * a changed payload be recorded as unchanged.
 */
export interface AuthoredAreaContent {
  readonly areaId: string;
  readonly name: string;
  readonly repoKey: string;
  readonly pathPrefixes: readonly string[];
  readonly references?: readonly { label: string; url: string }[] | undefined;
}

/**
 * The content fingerprint an idempotent seed compares on.
 *
 * Every authored field, length-prefixed and order-independent over the
 * collections. A re-seed of identical content must write nothing, and a
 * re-seed of DIFFERENT content must be a new revision — so this must change
 * for any authored difference and for no incidental one, including the order
 * a caller happened to list the prefixes in.
 */
export function areaContentHash(area: Readonly<AuthoredAreaContent>): string {
  return [
    'area-content-v1',
    canonicalString(area.areaId),
    canonicalString(area.name),
    canonicalString(area.repoKey.toLowerCase()),
    // Length-prefixed per member, not comma-joined: `['a/','b/']` and
    // `['a/,b/']` are different areas and must not share a hash, or a re-seed
    // that widened the boundary would be recorded as `unchanged`.
    canonicalList(
      [...area.pathPrefixes]
        .map((prefix) => normaliseAreaPrefix(prefix))
        .toSorted(),
    ),
    canonicalList(
      [...(area.references ?? [])]
        .map((reference) => canonicalList([reference.label, reference.url]))
        .toSorted(),
    ),
  ].join(':');
}
